import {
  type IFlagsmithFeature,
  useFlags,
  useFlagsmith,
} from '@flagsmith/flagsmith/react';
import { useEffect } from 'react';
import type { ActiveOrganizationProps } from '../providers/userContextProvider';

export enum FlagsmithFlags {
  workflow_review = 'workflow_review',
  auto_invite = 'auto_invite',
  multi_deposit_review = 'multi_deposit_review',
  repcode_access = 'repcode_access',
  configurable_table_pages = 'configurable_table_pages',
  mackenzie_account_page = 'mackenzie_account_page',
  conditional_account_page_configuration = 'conditional_account_page_configuration',
  acb_rework = 'acb_rework',
  spanish_language_support = 'spanish_language_support',
  weekdays_only_calendar = 'weekdays_only_calendar',
  text_variants = 'text_variants',
  form_mapper = 'form_mapper',
  show_account_type = 'show_account_type',
  portfolio_trading_prototype = 'portfolio_trading_prototype',
  widgets_additional_info = 'widgets_additional_info',
  close_sub_account = 'close_sub_account',
  alert_banners = 'alert_banners',
  asset_class_portfolios = 'asset_class_portfolios',
  audit_log_widget_v2 = 'audit_log_widget_v2',
  bulk_trader_revamp = 'bulk_trader_revamp',
  docusign_in_app_signing = 'docusign_in_app_signing',
  recon_flags_dashboard = 'recon_flags_dashboard',
  household_portfolio = 'household_portfolio',
  household_portfolio_allocation_methodology = 'household_portfolio_allocation_methodology',
  forms_idempotent_form_steps = 'forms_idempotent_form_steps',
}

export const useOrganizationWithFlagsmith = (
  organization: ActiveOrganizationProps,
) => {
  const flagsmithFlags = useFlags(Object.values(FlagsmithFlags));
  const flagsmith = useFlagsmith();
  // Use publicParents instead of parents to get the full inheritance chain
  // publicParents provides public info about parent organizations going up
  // the full inheritance chain to the top level, regardless of user permissions
  const organizationParentsTrait = (organization.publicParents || [])
    .map((p) => p.id)
    .join('/');
  useEffect(() => {
    flagsmith.identify(`org:${organization.id}`, {
      name: organization.translatedName?.en || null,
      parents: organizationParentsTrait,
    });
  }, [
    organization.id,
    organizationParentsTrait,
    organization.translatedName?.en,
    flagsmith,
  ]);
  const enabledFlagsmithFlags = Object.entries(flagsmithFlags)
    .filter(([, state]) => (state as IFlagsmithFeature).enabled)
    .map(([key]) => key);
  return {
    ...organization,
    availableFeatureFlags: [
      ...(organization.availableFeatureFlags ?? []),
      ...enabledFlagsmithFlags,
    ].filter(Boolean),
  };
};
